import { api } from './api';
import { API_CONFIG } from '../config';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { categoryService } from './categoryService';

// Définition du type ApiEndpoints avec ADMIN optionnel
interface ApiEndpoints {
  EVENTS: string;
  ORGANIZATIONS: string;
  USERS: string;
  STAFF: string;
  EVENT_CATEGORIES: string;
};

// Récupération des endpoints depuis la configuration
const ENDPOINTS = API_CONFIG.ENDPOINTS as unknown as ApiEndpoints;

// Point d'accès pour les fonctionnalités d'administration
const EVENTS_ENDPOINT = ENDPOINTS.EVENTS;
const USERS_ENDPOINT = ENDPOINTS.USERS;
const ORGANIZATIONS_ENDPOINT = ENDPOINTS.ORGANIZATIONS;
const EVENT_CATEGORIES_ENDPOINT = ENDPOINTS.EVENT_CATEGORIES || '/event-categories'; // Modification ici pour utiliser le bon endpoint

// Structure d'un événement pour l'importation Excel
export interface EventTemplate {
  title: string;
  description: string;
  dateTime: string;
  location: string;
  address?: string; 
  latitude?: number; 
  longitude?: number; 
  category: string;
  tags?: string;
  maxParticipants?: number;
  organizationId: number;
  imageUrl?: string;
  isActive?: boolean; 
}

// Service d'administration pour gérer les utilisateurs, événements et organisations
export const adminService = {
  // Gestion des utilisateurs
  async getAllUsers(params?: { role?: string; search?: string }): Promise<any[]> {
    try {
      const { data } = await api.get(`${USERS_ENDPOINT}`, { params });
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw error;
    }
  },

  async getUserById(id: number): Promise<any> {
    try {
      const { data } = await api.get(`${USERS_ENDPOINT}/${id}`);
      return data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'utilisateur ${id}:`, error);
      throw error;
    }
  },

  async createUser(userData: any): Promise<any> {
    try {
      const { data } = await api.post(`${USERS_ENDPOINT}`, userData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      throw error;
    }
  },

  async updateUser(id: number, userData: any): Promise<any> {
    try {
      const { data } = await api.put(`${USERS_ENDPOINT}/${id}`, userData);
      return data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'utilisateur ${id}:`, error);
      throw error;
    }
  },

  async deleteUser(id: number): Promise<void> {
    try {
      await api.delete(`${USERS_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'utilisateur ${id}:`, error);
      throw error;
    }
  },

  // Gestion des événements
  async getAllEvents(params?: { category?: string; search?: string; organizationId?: number }): Promise<any[]> {
    try {
      const { data } = await api.get(`${EVENTS_ENDPOINT}`, { params });
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des événements:', error);
      throw error;
    }
  },

  async getEventById(id: number): Promise<any> {
    try {
      const { data } = await api.get(`${EVENTS_ENDPOINT}/${id}`);
      return data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'événement ${id}:`, error);
      throw error;
    }
  },

  async createEvent(eventData: any): Promise<any> {
    try {
      const { data } = await api.post(`${EVENTS_ENDPOINT}`, eventData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'événement:', error);
      throw error;
    }
  },

  async updateEvent(id: number, eventData: any): Promise<any> {
    try {
      console.log('Données initiales pour mise à jour:', eventData);
      
      // Créer un objet propre avec seulement les propriétés attendues par le backend
      const cleanedData: {
        title: any;
        description: any;
        dateTime: any;
        location: any;
        maxParticipants: any;
        tags: any;
        latitude: any;
        longitude: any;
        imageUrl: any;
        organizationId: any;
        categoryId?: any;
      } = {
        title: eventData.title,
        description: eventData.description,
        dateTime: eventData.dateTime,
        location: eventData.location,
        maxParticipants: eventData.maxParticipants,
        tags: eventData.tags,
        latitude: eventData.latitude,
        longitude: eventData.longitude,
        imageUrl: eventData.imageUrl,
        organizationId: eventData.organizationId
      };
      
      // Ajouter la catégorie si elle est spécifiée
      if (eventData.categoryId) {
        cleanedData.categoryId = eventData.categoryId;
      }
      
      console.log('Données nettoyées pour mise à jour:', cleanedData);
      
      const { data } = await api.put(`${EVENTS_ENDPOINT}/${id}`, cleanedData);
      return data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'événement ${id}:`, error);
      throw error;
    }
  },

  async deleteEvent(id: number): Promise<void> {
    try {
      await api.delete(`${EVENTS_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'événement ${id}:`, error);
      throw error;
    }
  },

  // Gestion des organisations
  async getAllOrganizations(params?: { search?: string; status?: string }): Promise<any[]> {
    try {
      const { data } = await api.get(`${ORGANIZATIONS_ENDPOINT}`, { params });
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des organisations:', error);
      throw error;
    }
  },

  async getOrganizationById(id: number): Promise<any> {
    try {
      const { data } = await api.get(`${ORGANIZATIONS_ENDPOINT}/${id}`);
      return data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'organisation ${id}:`, error);
      throw error;
    }
  },

  async createOrganization(orgData: any): Promise<any> {
    try {
      const { data } = await api.post(`${ORGANIZATIONS_ENDPOINT}`, orgData);
      return data;
    } catch (error) {
      console.error('Erreur lors de la création de l\'organisation:', error);
      throw error;
    }
  },

  async updateOrganization(id: number, orgData: any): Promise<any> {
    try {
      const { data } = await api.put(`${ORGANIZATIONS_ENDPOINT}/${id}`, orgData);
      return data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de l'organisation ${id}:`, error);
      throw error;
    }
  },

  async deleteOrganization(id: number): Promise<void> {
    try {
      await api.delete(`${ORGANIZATIONS_ENDPOINT}/${id}`);
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'organisation ${id}:`, error);
      throw error;
    }
  },

  async verifyOrganization(id: number, isVerified: boolean): Promise<any> {
    try {
      const { data } = await api.patch(`${ORGANIZATIONS_ENDPOINT}/${id}/verify`, { isVerified });
      return data;
    } catch (error) {
      console.error(`Erreur lors de la modification du statut de vérification de l'organisation ${id}:`, error);
      throw error;
    }
  },

  // Stats et tableaux de bord
  async getDashboardStats(): Promise<any> {
    try {
      const usersPromise = this.getUsersStats();
      const eventsPromise = this.getEventsStats();
      const orgsPromise = this.getOrganizationsStats();
      
      const [users, events, organizations] = await Promise.allSettled([
        usersPromise, eventsPromise, orgsPromise
      ]);
      
      return {
        users: users.status === 'fulfilled' ? users.value : {},
        events: events.status === 'fulfilled' ? events.value : {},
        organizations: organizations.status === 'fulfilled' ? organizations.value : {}
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques du tableau de bord:', error);
      throw error;
    }
  },

  async getUsersStats(): Promise<any> {
    try {
      const { data } = await api.get(`${USERS_ENDPOINT}`);
      
      const total = data?.length || 0;
      const active = data?.filter((user: any) => user.isActive !== false)?.length || 0;
      
      const roles = {
        student: 0,
        admin: 0,
        organization: 0
      };
      
      data?.forEach((user: any) => {
        const role = user.role || user.userType || 'student';
        if (role === 'admin' || role === 'org_admin') {
          roles.admin++;
        } else if (role === 'organization') {
          roles.organization++;
        } else {
          roles.student++;
        }
      });
      
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const newThisMonth = data?.filter((user: any) => {
        const createdAt = user.createdAt ? new Date(user.createdAt) : null;
        return createdAt && createdAt >= thisMonth;
      })?.length || 0;
      
      return {
        total,
        active,
        newThisMonth,
        byRole: roles
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des utilisateurs:', error);
      throw error;
    }
  },

  async getEventsStats(): Promise<any> {
    try {
      const { data: categoryStats } = await api.get(`/event-categories/stats`);
      const { data: events } = await api.get(`${EVENTS_ENDPOINT}`);
      
      const total = events?.length || 0;
      
      const now = new Date();
      const upcoming = events?.filter((event: any) => {
        const eventDate = event.dateTime ? new Date(event.dateTime) : null;
        return eventDate && eventDate >= now;
      })?.length || 0;
      
      const byCategory: Record<string, number> = {
        'conference': 0,
        'workshop': 0,
        'meetup': 0,
        'hackathon': 0,
        'webinar': 0,
        'other': 0
      };
      
      if (Array.isArray(categoryStats)) {
        categoryStats.forEach((stat: any) => {
          const code = stat.code;
          if (code in byCategory) {
            byCategory[code] = stat.count;
          } else if (code === 'soiree' || code === 'festival') {
            byCategory['meetup'] += stat.count;
          } else {
            byCategory['other'] += stat.count;
          }
        });
      } else if (Array.isArray(events) && events.length > 0) {
        events.forEach((event: any) => {
          const rawCategory = event.eventCategory ? event.eventCategory.name : 'Autre';
          
          const mappedCategory = categoryService.normalizeCategory(rawCategory);
          
          if (mappedCategory in byCategory) {
            byCategory[mappedCategory]++;
          } else {
            byCategory['other']++;
          }
        });
      }
      
      return {
        total,
        upcoming,
        byCategory
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques d\'événements:', error);
      return {
        total: 0,
        upcoming: 0,
        byCategory: {
          'conference': 0,
          'workshop': 0,
          'meetup': 0,
          'hackathon': 0,
          'webinar': 0,
          'other': 0
        }
      };
    }
  },

  async getOrganizationsStats(): Promise<any> {
    try {
      const { data } = await api.get(`${ORGANIZATIONS_ENDPOINT}`);
      
      const total = data?.length || 0;
      
      const verified = data?.filter((org: any) => 
        org.isVerified === true || 
        (org._count?.events && org._count.events > 0)
      )?.length || 0;
      
      const active = data?.filter((org: any) => 
        org.isActive !== false
      )?.length || 0;
      
      return {
        total,
        verified,
        active
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des organisations:', error);
      throw error;
    }
  },

  async migrateEventCategories(sourceId: string, targetId: string): Promise<{ success: number; errors: any[] }> {
    try {
      console.log(`Migration des événements de la catégorie ${sourceId} vers ${targetId}`);
      
      const { data } = await api.post(`${EVENTS_ENDPOINT}/migrate-by-code`, {
        sourceCategory: sourceId,
        targetCategory: targetId
      });
      
      console.log('Résultat de la migration:', data);
      
      return {
        success: data.migratedCount || 0,
        errors: data.errors || []
      };
    } catch (error) {
      console.error('Erreur lors de la migration des événements:', error);
      throw error;
    }
  },

  async importEventsFromExcel(file: File): Promise<{ success: any[]; errors: any[] }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await api.post(`${EVENTS_ENDPOINT}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return data;
    } catch (error) {
      console.error('Erreur lors de l\'importation des événements:', error);
      throw error;
    }
  },

  async generateEventTemplate(): Promise<void> {
    try {
      const response = await api.get(`${EVENT_CATEGORIES_ENDPOINT}`);
      const categories = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.categories || []);
      
      const workbook = XLSX.utils.book_new();
      
      const templateData = [
        ["Titre*", "Description*", "Date (YYYY-MM-DD)*", "Heure (HH:MM)*", "Lieu*", "Catégorie*", "Tags", "Max Participants", "Organisation ID*"]
      ];
      
      templateData.push([
        "Conférence sur l'IA", 
        "Une conférence passionnante sur l'intelligence artificielle", 
        "2023-12-01", 
        "14:30", 
        "Campus des Sciences, Bâtiment C, Salle 105", 
        categories.length > 0 ? categories[0].code : "CONFERENCE", 
        "tech, ia, innovation", 
        "150", 
        "1"
      ]);
      
      templateData.push([
        "Workshop Design Thinking", 
        "Apprenez les méthodes de design thinking à travers des exercices pratiques", 
        "2023-12-15", 
        "09:00", 
        "Espace de coworking CreativHub", 
        categories.length > 1 ? categories[1].code : "WORKSHOP", 
        "design, innovation, collaboration", 
        "30", 
        "2"
      ]);
      
      const templateSheet = XLSX.utils.aoa_to_sheet(templateData);
      XLSX.utils.book_append_sheet(workbook, templateSheet, "Modèle");
      
      const instructionsData = [
        ["Champ", "Type", "Obligatoire", "Description"],
        ["Titre", "Texte", "Oui", "Nom de l'événement"],
        ["Description", "Texte", "Oui", "Description détaillée"],
        ["Date", "YYYY-MM-DD", "Oui", "Date au format année-mois-jour"],
        ["Heure", "HH:MM", "Oui", "Heure au format 24h"],
        ["Lieu", "Texte", "Oui", "Adresse ou lieu de l'événement"],
        ["Catégorie", "Code", "Oui", "Code de catégorie (voir feuille Catégories)"],
        ["Tags", "Texte", "Non", "Mots-clés séparés par des virgules"],
        ["Max Participants", "Nombre", "Non", "Limite de participants"],
        ["organizationId", "Nombre", "Oui", "ID de l'organisation"],
        ["imageUrl", "URL", "Non", "URL de l'image"],
        ["isActive", "true/false", "Non", "Statut d'activation (défaut: true)"]
      ];
      
      const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
      XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

      const categoriesData = [
        ["ID", "Nom", "Description", "Code Legacy"]
      ];
      
      interface CategoryType {
        id: number;
        code: string;
        name: string;
        description?: string;
        color?: string;
        icon?: string;
        isActive?: boolean;
      }
      
      if (categories.length > 0) {
        categories.forEach((category: CategoryType) => {
          categoriesData.push([
            category.code,
            category.name,
            category.description || "",
            category.code.toLowerCase() 
          ]);
        });
      } else {
        categoriesData.push(
          ["CONFERENCE", "Conférence", "Présentations formelles sur un sujet spécifique", "conference"],
          ["WORKSHOP", "Atelier", "Sessions pratiques et interactives", "workshop"],
          ["MEETUP", "Meetup", "Rencontres informelles entre personnes partageant les mêmes intérêts", "meetup"],
          ["HACKATHON", "Hackathon", "Événements de programmation collaborative", "hackathon"],
          ["WEBINAR", "Webinaire", "Séminaires en ligne", "webinar"],
          ["SOCIAL", "Social", "Événements de réseautage et événements sociaux", "social"],
          ["CULTURAL", "Culturel", "Événements artistiques et culturels", "cultural"],
          ["SPORT", "Sport", "Activités sportives et récréatives", "sport"],
          ["OTHER", "Autre", "Autres types d'événements non catégorisés", "other"]
        );
      }
      
      const categoriesSheet = XLSX.utils.aoa_to_sheet(categoriesData);
      XLSX.utils.book_append_sheet(workbook, categoriesSheet, "Catégories");
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(data, 'modele_import_evenements.xlsx');
    } catch (error) {
      console.error('Erreur lors de la génération du modèle:', error);
      throw error;
    }
  },

  async processExcelEvents(file: File): Promise<{ success: any[]; errors: any[] }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const result: { success: any[]; errors: any[] } = { success: [], errors: [] };
      
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet);
          
          if (json.length === 0) {
            reject(new Error('La feuille Excel est vide.'));
            return;
          }
          
          console.log("Nombre de lignes Excel:", json.length);
          
          // Récupérer les catégories disponibles
          let categories = [];
          try {
            const categoriesResponse = await api.get(EVENT_CATEGORIES_ENDPOINT);
            categories = categoriesResponse.data;
          } catch (error) {
            console.warn('Impossible de récupérer les catégories, utilisation des valeurs par défaut:', error);
            // Utiliser des catégories par défaut en cas d'échec
            categories = [
              { id: 1, name: 'Concert', code: 'concert' },
              { id: 2, name: 'Festival', code: 'festival' },
              { id: 3, name: 'Spectacle', code: 'spectacle' },
              { id: 4, name: 'Atelier', code: 'atelier' },
              { id: 5, name: 'Autre', code: 'autre' }
            ];
          }
          
          for (let i = 0; i < json.length; i++) {
            try {
              const processedEvent = await this.processEventData(json[i], categories);
              
              try {
                const createdEvent = await this.adminImportEvent(processedEvent);
                result.success.push(createdEvent);
              } catch (error: any) {
                console.error('Échec de l\'import Excel:', error);
                result.errors.push({
                  line: i + 2, // +2 car i commence à 0 et l'en-tête est ligne 1
                  data: processedEvent,
                  error: error.message || 'Erreur lors de la création de l\'événement'
                });
              }
            } catch (error: any) {
              console.error(`Erreur lors du traitement de la ligne Excel ${i + 2}:`, error);
              result.errors.push({
                line: i + 2,
                data: json[i],
                error: error.message || 'Erreur inconnue'
              });
            }
          }
          
          resolve(result);
          
        } catch (error: any) {
          console.error('Erreur lors du traitement du fichier Excel:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.readAsBinaryString(file);
    });
  },
  
  async processCsvEvents(file: File): Promise<{ success: any[]; errors: any[] }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const result: { success: any[]; errors: any[] } = { success: [], errors: [] };
      
      reader.onload = async (e) => {
        try {
          const csvContent = e.target?.result as string;
          if (!csvContent) {
            reject(new Error('Le fichier CSV est vide.'));
            return;
          }
          
          // Diviser par lignes, ignorer les lignes vides en fin de fichier
          const lines = csvContent.split('\n').filter(line => line.trim() !== '');
          if (lines.length < 2) {
            reject(new Error('Le fichier CSV ne contient pas suffisamment de données.'));
            return;
          }
          
          console.log("Nombre de lignes CSV:", lines.length);
          
          // Meilleur parsing des en-têtes pour gérer les guillemets correctement
          const headers = this.parseCSVRow(lines[0]);
          console.log("En-têtes CSV détectés:", headers);
          
          const categoriesResponse = await api.get(`${EVENT_CATEGORIES_ENDPOINT}`);
          const categories = Array.isArray(categoriesResponse.data) 
            ? categoriesResponse.data 
            : (categoriesResponse.data?.categories || []);
          
          console.log('Catégories récupérées:', categories); 
          
          for (let i = 1; i < lines.length; i++) {
            if (!lines[i].trim()) continue; 
            
            try {
              // Utiliser la nouvelle méthode de parsing qui gère correctement les CSV
              const cells = this.parseCSVRow(lines[i]);
              console.log(`Ligne ${i} - cellules analysées:`, cells);
              
              if (cells.length < headers.length) {
                console.log(`Ligne ${i} - Nombre de cellules incomplet, ajout de valeurs vides. ${cells.length}/${headers.length}`);
                cells.push(...Array(headers.length - cells.length).fill(''));
              }
              
              const eventData: Record<string, any> = {};
              headers.forEach((header, index) => {
                if (index < cells.length) {
                  // Nettoyer les valeurs des guillemets superflus
                  let value = cells[index];
                  if (value.startsWith('"') && value.endsWith('"')) {
                    value = value.substring(1, value.length - 1);
                  }
                  
                  // Convertir les valeurs numériques en nombres pour latitude/longitude
                  if ((header === 'Latitude' || header === 'Longitude') && value) {
                    eventData[header] = parseFloat(value);
                  } else {
                    eventData[header] = value;
                  }
                }
              });
              
              console.log(`Ligne ${i} - Données d'événement mappées:`, eventData);
              
              const processedEvent = await this.processEventData(eventData, categories);
              console.log(`Ligne ${i} - Événement traité avant envoi:`, processedEvent);
              
              try {
                const createdEvent = await this.adminImportEvent(processedEvent);
                result.success.push(createdEvent);
              } catch (error: any) {
                console.error('Échec de l\'import:', error);
                result.errors.push({
                  line: i,
                  data: processedEvent,
                  error: error.message || 'Erreur lors de la création de l\'événement'
                });
              }
            } catch (error: any) {
              console.error(`Erreur lors du traitement de la ligne ${i}:`, error);
              result.errors.push({
                line: i,
                data: lines[i],
                error: error.message || 'Erreur inconnue'
              });
            }
          }
          
          resolve(result);
        } catch (error: any) {
          console.error('Erreur lors du traitement du fichier CSV:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.readAsText(file);
    });
  },
  
  // Méthode robuste pour analyser une ligne CSV, gère les champs entre guillemets contenant des virgules
  parseCSVRow(text: string): string[] {
    // Cas spécial : si la ligne est vide, retourner un tableau vide
    if (!text || text.trim() === '') {
      return [];
    }
    
    const result: string[] = [];
    let cell = '';
    let insideQuotes = false;
    
    console.log("Traitement ligne CSV brute:", text);
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      
      if (char === '"') {
        // Si nous trouvons un guillemet échappé (double guillemet) à l'intérieur d'un champ entre guillemets
        if (insideQuotes && text[i+1] === '"') {
          cell += '"';
          i++; // Sauter le prochain guillemet (partie de l'échappement)
        } else {
          // Basculer l'état insideQuotes
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        // Si virgule hors guillemets = séparateur de champ
        result.push(cell.trim());
        cell = '';
      } else {
        cell += char;
      }
    }
    
    // Ne pas oublier la dernière cellule
    result.push(cell.trim());
    
    console.log("Résultat parsing:", result);
    
    return result;
  },
  
  async processEventData(eventData: any, categories: any[]): Promise<any> {
    const processedEvent: any = {};
    
    // Vérifier et s'assurer que categories est bien un tableau
    const categoriesArray = Array.isArray(categories) ? categories : [];
    
    // Correspondance des noms de colonnes (modèle CSV vs modèle de données)
    const columnMapping: Record<string, string> = {
      'Titre': 'title',
      'Description': 'description',
      'Date et Heure (YYYY-MM-DD HH:MM)': 'dateTime',
      'Date (YYYY-MM-DD)': 'date',
      'Heure (HH:MM)': 'time',
      'Lieu': 'location',
      'Catégorie': 'category',
      'Tags': 'tags',
      'Nombre maximum de participants': 'maxParticipants',
      'Organisation ID': 'organizationId',
      'Latitude': 'latitude',
      'Longitude': 'longitude',
      'URL de l\'image': 'imageUrl'
    };
    
    // Mapper les données en utilisant la correspondance des colonnes
    Object.entries(eventData).forEach(([key, value]) => {
      const mappedKey = columnMapping[key] || key;
      processedEvent[mappedKey] = value;
    });
    
    // S'assurer que latitude et longitude sont des nombres
    if (eventData['Latitude']) {
      processedEvent.latitude = parseFloat(eventData['Latitude']);
    }
    
    if (eventData['Longitude']) {
      processedEvent.longitude = parseFloat(eventData['Longitude']);
    }
    
    // S'assurer que l'URL de l'image est correctement mappée
    if (eventData['URL de l\'image']) {
      processedEvent.imageUrl = eventData['URL de l\'image'];
    }
    
    if (!processedEvent.organizationId) {
      processedEvent.organizationId = 1;
    } else if (typeof processedEvent.organizationId === 'string') {
      processedEvent.organizationId = parseInt(processedEvent.organizationId);
    }
    
    // Gérer les formats de date variés
    if (processedEvent.date && processedEvent.time) {
      // Format date et heure séparés
      processedEvent.dateTime = `${processedEvent.date}T${processedEvent.time}:00`;
      delete processedEvent.date;
      delete processedEvent.time;
    } else if (processedEvent.dateTime) {
      // Format date et heure combinés (YYYY-MM-DD HH:MM)
      const dateTimeParts = processedEvent.dateTime.toString().trim().split(' ');
      
      if (dateTimeParts.length === 2) {
        const [datePart, timePart] = dateTimeParts;
        
        // S'assurer que l'heure est au format HH:MM:SS (ajouter des zéros si nécessaire)
        let formattedTime = timePart;
        if (formattedTime.split(':').length === 1) {
          formattedTime = `${formattedTime}:00`;
        }
        if (formattedTime.split(':').length === 2) {
          formattedTime = `${formattedTime}:00`;
        }
        
        // Créer la date au format ISO
        processedEvent.dateTime = `${datePart}T${formattedTime}`;
      }
    } else {
      // Si aucune date n'est fournie, utiliser la date actuelle + 1 semaine
      const oneWeekLater = new Date();
      oneWeekLater.setDate(oneWeekLater.getDate() + 7);
      processedEvent.dateTime = oneWeekLater.toISOString().split('.')[0];
    }
    
    // Restaurer les valeurs par défaut pour les champs manquants
    if (!processedEvent.title) {
      processedEvent.title = "Nouvel événement importé";
    }
    
    if (!processedEvent.description) {
      processedEvent.description = "Description à compléter";
    }
    
    if (!processedEvent.location) {
      processedEvent.location = "À définir";
    }
    
    // Traitement spécial pour le fichier CSV fourni qui utilise "concert", "festival", etc.
    if (processedEvent.category) {
      // Correspondance directe des valeurs du CSV aux IDs de catégorie
      const categoryMapping: Record<string, number> = {
        'concert': 1,
        'festival': 2,
        'spectacle': 3,
        'atelier': 4,
        'autre': 5
      };
      
      const categoryLower = processedEvent.category.toLowerCase();
      if (categoryMapping[categoryLower]) {
        processedEvent.categoryId = categoryMapping[categoryLower];
      } else {
        // Recherche dans la liste des catégories
        const matchedCategory = categoriesArray.find(cat => 
          cat && (
            cat.code === processedEvent.category || 
            cat.name === processedEvent.category ||
            (cat.code && cat.code.toLowerCase() === categoryLower) ||
            (cat.name && cat.name.toLowerCase() === categoryLower)
          )
        );
        
        if (matchedCategory) {
          processedEvent.categoryId = typeof matchedCategory.id === 'string' 
            ? parseInt(matchedCategory.id) 
            : matchedCategory.id;
        } else {
          // Valeur par défaut
          processedEvent.categoryId = 5; // Autre
        }
      }
      
      // Supprimer le champ category pour éviter les conflits
      delete processedEvent.category;
    } else {
      // Valeur par défaut
      processedEvent.categoryId = 5; // Autre
    }
    
    // Suppression des champs qui ne sont pas attendus par le backend
    delete processedEvent.eventCategory;
    
    // Traiter maxParticipants (peut être null)
    if (processedEvent.maxParticipants) {
      processedEvent.maxParticipants = parseInt(String(processedEvent.maxParticipants));
      // Si la valeur est NaN ou 0, utiliser null
      if (isNaN(processedEvent.maxParticipants) || processedEvent.maxParticipants <= 0) {
        processedEvent.maxParticipants = null;
      }
    } else {
      processedEvent.maxParticipants = null; // Permettre null
    }
    
    if (processedEvent.tags) {
      if (Array.isArray(processedEvent.tags)) {
        processedEvent.tags = processedEvent.tags.join(',');
      } else if (typeof processedEvent.tags === 'string') {
        // Pas besoin de conversion - déjà une chaîne
      } else {
        processedEvent.tags = '';
      }
    }
    
    delete processedEvent.id;
    delete processedEvent.createdAt;
    delete processedEvent.updatedAt;
    
    console.log('Événement transformé:', processedEvent);
    
    return processedEvent;
  },
  
  async adminImportEvent(eventData: any): Promise<any> {
    try {
      console.log('Données envoyées à l\'API (avant nettoyage):', eventData);
      
      // S'assurer que la date est au bon format ISO 8601
      let formattedDateTime = eventData.dateTime;
      
      // Si la date ne contient que l'heure (HH:MM:SS), ajouter la date (aujourd'hui)
      if (formattedDateTime && formattedDateTime.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
        const today = new Date().toISOString().split('T')[0];
        formattedDateTime = `${today}T${formattedDateTime}`;
        // S'assurer que les secondes sont présentes
        if (formattedDateTime.split(':').length < 3) {
          formattedDateTime = `${formattedDateTime}:00`;
        }
      }
      // Vérifier si la date est au format "YYYY-MM-DD HH:MM" (avec espace)
      else if (formattedDateTime && formattedDateTime.includes(' ')) {
        formattedDateTime = formattedDateTime.replace(' ', 'T') + ':00';
      } 
      // Si la date ne contient pas de "T" ou de ":", c'est probablement juste une date
      else if (formattedDateTime && !formattedDateTime.includes('T') && !formattedDateTime.includes(':')) {
        // Ajouter T00:00:00 pour en faire une date+heure valide
        formattedDateTime = `${formattedDateTime}T00:00:00`;
      }
      // Si la date contient T mais pas de secondes
      else if (formattedDateTime && formattedDateTime.includes('T') && formattedDateTime.split(':').length < 3) {
        // Ajouter les secondes
        formattedDateTime = `${formattedDateTime}:00`;
      }

      // Si toujours pas de date valide, utiliser la date actuelle
      if (!formattedDateTime || !formattedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/)) {
        const now = new Date();
        now.setDate(now.getDate() + 7); // Date par défaut = aujourd'hui + 7 jours
        formattedDateTime = now.toISOString().split('.')[0];
      }
      
      // Convertir les tags en chaîne si c'est un tableau (backend attend une chaîne)
      let tagString = '';
      if (eventData.tags) {
        if (Array.isArray(eventData.tags)) {
          tagString = eventData.tags.join(',');
        } else if (typeof eventData.tags === 'string') {
          tagString = eventData.tags;
        }
      }
      
      // Nettoyage des données pour garantir le format attendu
      const cleanedData = {
        title: eventData.title || 'Événement sans titre',
        description: eventData.description || 'Aucune description',
        dateTime: formattedDateTime,
        location: eventData.location || 'À définir',
        organizationId: 1, // Forcer à 1 pour contourner les vérifications
        categoryId: eventData.categoryId ? Number(eventData.categoryId) : 1, // S'assurer que c'est un nombre
        maxParticipants: eventData.maxParticipants ? parseInt(eventData.maxParticipants) : null, // Permettre null
        tags: tagString, // Envoyer comme chaîne
        imageUrl: eventData.imageUrl || null,
        latitude: eventData.latitude ? parseFloat(eventData.latitude) : null,
        longitude: eventData.longitude ? parseFloat(eventData.longitude) : null
      };
      
      console.log('Données envoyées à l\'API (après nettoyage):', cleanedData);
      
      // Utilisation directe de l'endpoint standard
      try {
        const { data } = await api.post(`${EVENTS_ENDPOINT}`, cleanedData);
        console.log('Réponse de l\'API d\'import standard:', data);
        return data;
      } catch (error: any) {
        // Afficher les détails de l'erreur pour mieux comprendre le problème
        if (error.response && error.response.data) {
          console.error('Détails de l\'erreur:', error.response.data);
        }
        console.error('Erreur lors de l\'import standard:', error);
        throw error;
      }
    } catch (error) {
      console.error('Erreur lors de l\'import admin:', error);
      throw error;
    }
  },

  async importEvent(eventData: any): Promise<any> {
    try {
      // Préparation et nettoyage des données
      let formattedDateTime = eventData.dateTime;
      
      // Gérer les différents formats de date possible
      if (formattedDateTime instanceof Date) {
        formattedDateTime = formattedDateTime.toISOString().split('.')[0];
      } 
      else if (formattedDateTime && formattedDateTime.includes(' ')) {
        formattedDateTime = formattedDateTime.replace(' ', 'T') + ':00';
      } 
      // Si la date ne contient pas de "T" ou de ":", c'est probablement juste une date
      else if (formattedDateTime && !formattedDateTime.includes('T') && !formattedDateTime.includes(':')) {
        // Ajouter T00:00:00 pour en faire une date+heure valide
        formattedDateTime = `${formattedDateTime}T00:00:00`;
      }
      // Si la date contient T mais pas de secondes
      else if (formattedDateTime && formattedDateTime.includes('T') && formattedDateTime.split(':').length < 3) {
        // Ajouter les secondes
        formattedDateTime = `${formattedDateTime}:00`;
      }

      // Si toujours pas de date valide, utiliser la date actuelle
      if (!formattedDateTime || !formattedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/)) {
        const now = new Date();
        now.setDate(now.getDate() + 7); // Date par défaut = aujourd'hui + 7 jours
        formattedDateTime = now.toISOString().split('.')[0];
      }
      
      // Convertir les tags en chaîne si c'est un tableau (backend attend une chaîne)
      let tagString = '';
      if (eventData.tags) {
        if (Array.isArray(eventData.tags)) {
          tagString = eventData.tags.join(',');
        } else if (typeof eventData.tags === 'string') {
          tagString = eventData.tags;
        }
      }
      
      // Nettoyage des données pour garantir le format attendu par l'API
      const cleanedData = {
        title: eventData.title || 'Événement sans titre',
        description: eventData.description || 'Aucune description',
        dateTime: formattedDateTime,
        location: eventData.location || 'À définir',
        organizationId: eventData.organizationId || 1,
        categoryId: eventData.categoryId ? Number(eventData.categoryId) : 1,
        maxParticipants: eventData.maxParticipants ? parseInt(eventData.maxParticipants) : null,
        tags: tagString,
        imageUrl: eventData.imageUrl || null,
        latitude: eventData.latitude ? parseFloat(eventData.latitude) : null,
        longitude: eventData.longitude ? parseFloat(eventData.longitude) : null,
        isActive: eventData.isActive !== undefined ? eventData.isActive : true
      };
      
      // Créer l'événement via l'API
      const { data } = await api.post(`${EVENTS_ENDPOINT}`, cleanedData);
      return data;
    } catch (error) {
      console.error('Erreur lors de l\'import d\'événement:', error);
      throw error;
    }
  },

  // Récupérer toutes les catégories d'événements
  async getAllEventCategories(): Promise<any[]> {
    try {
      console.log('Appel de l\'API pour récupérer les catégories d\'événements:', EVENT_CATEGORIES_ENDPOINT);
      const { data } = await api.get(EVENT_CATEGORIES_ENDPOINT);
      console.log('Catégories récupérées:', data);
      return data;
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories d\'événements:', error);
      // En cas d'erreur, retourner les catégories par défaut
      return [
        { id: 1, name: 'Concert', color: '#4CAF50', code: 'concert' },
        { id: 2, name: 'Festival', color: '#2196F3', code: 'festival' },
        { id: 3, name: 'Spectacle', color: '#9C27B0', code: 'spectacle' },
        { id: 4, name: 'Atelier', color: '#FF9800', code: 'atelier' },
        { id: 5, name: 'Autre', color: '#607D8B', code: 'autre' }
      ];
    }
  },

  // Exporter les événements au format JSON
  async exportEventsToJson(params?: { category?: string; search?: string; organizationId?: number }): Promise<any> {
    try {
      // Récupération des événements à exporter
      const events = await this.getAllEvents(params);
      
      // Création du fichier JSON
      const jsonData = JSON.stringify(events, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const fileName = `evenements-export_${new Date().toISOString().split('T')[0]}.json`;
      
      // Téléchargement du fichier
      saveAs(blob, fileName);
      
      return { success: true, count: events.length };
    } catch (error) {
      console.error('Erreur lors de l\'exportation des événements en JSON:', error);
      throw error;
    }
  },
  
  // Exporter les événements au format CSV
  async exportEventsToCsv(params?: { category?: string; search?: string; organizationId?: number }): Promise<any> {
    try {
      // Récupération des événements à exporter
      const events = await this.getAllEvents(params);
      
      // Définition des en-têtes CSV
      const headers = [
        'Titre', 
        'Description', 
        'Date et Heure', 
        'Lieu', 
        'Latitude', 
        'Longitude', 
        'Catégorie', 
        'Tags', 
        'Participants Max', 
        'Organisation', 
        'Image URL', 
        'Actif'
      ];
      
      // Conversion des événements en lignes CSV
      const rows = events.map(event => {
        // Extraction des coordonnées uniquement de l'événement lui-même
        let latitude = '';
        let longitude = '';
        
        // Uniquement les coordonnées directes de l'événement
        if (event.latitude !== undefined && event.latitude !== null) {
          latitude = String(event.latitude);
        } else if (event.coordinates?.lat !== undefined && event.coordinates.lat !== null) {
          latitude = String(event.coordinates.lat);
        }
        
        if (event.longitude !== undefined && event.longitude !== null) {
          longitude = String(event.longitude);
        } else if (event.coordinates?.lon !== undefined && event.coordinates.lon !== null) {
          longitude = String(event.coordinates.lon);
        }
        
        // Ne pas utiliser les coordonnées de l'organisation
        // comme valeur par défaut, ce n'est pas correct
        
        return [
          event.title || '',
          event.description || '',
          event.dateTime || '',
          event.location || '',
          latitude,
          longitude,
          event.eventCategory?.name || '',
          event.tags || '',
          event.maxParticipants || '',
          event.organizationName || event.organization?.name || '',
          event.imageUrl || '',
          event.isActive ? 'Oui' : 'Non'
        ];
      });
      
      // Construire le contenu CSV
      let csvContent = headers.join(',') + '\n';
      
      // Fonction pour échapper les virgules et les guillemets dans les cellules
      const escapeCSV = (cell: any) => {
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      };
      
      rows.forEach(row => {
        csvContent += row.map(escapeCSV).join(',') + '\n';
      });
      
      // Création du fichier CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const fileName = `evenements-export_${new Date().toISOString().split('T')[0]}.csv`;
      
      // Téléchargement du fichier
      saveAs(blob, fileName);
      
      return { success: true, count: events.length };
    } catch (error) {
      console.error('Erreur lors de l\'exportation des événements en CSV:', error);
      throw error;
    }
  },
  
  // Importer des événements à partir d'un fichier JSON
  async processJsonEvents(file: File): Promise<{ success: any[]; errors: any[] }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          // Récupérer le contenu du fichier
          const fileContent = e.target?.result as string;
          let events: any[] = [];
          
          try {
            events = JSON.parse(fileContent);
            if (!Array.isArray(events)) {
              throw new Error('Le fichier JSON doit contenir un tableau d\'événements');
            }
          } catch (parseError) {
            console.error('Erreur de parsing JSON:', parseError);
            reject(new Error('Format JSON invalide. Assurez-vous que le fichier contient un tableau d\'événements.'));
            return;
          }
          
          // Vérifier que les données sont cohérentes
          if (events.length === 0) {
            reject(new Error('Le fichier JSON ne contient aucun événement.'));
            return;
          }
          
          // Préparer les résultats
          const results = {
            success: [] as any[],
            errors: [] as any[]
          };
          
          // Récupérer les catégories d'événements pour la vérification
          const categories = await this.getAllEventCategories();
          
          // Traiter chaque événement
          for (const event of events) {
            try {
              // Vérifier les champs requis
              if (!event.title) {
                throw new Error('Le titre est obligatoire');
              }
              
              // Mapper l'événement au format attendu par l'API
              const eventData: EventTemplate = {
                title: event.title,
                description: event.description || 'Aucune description',
                dateTime: event.dateTime || new Date().toISOString(),
                location: event.location || 'À définir',
                category: event.category || event.eventCategory?.name || 'Autre',
                maxParticipants: event.maxParticipants,
                organizationId: event.organizationId || 1,
                imageUrl: event.imageUrl,
                isActive: event.isActive !== undefined ? event.isActive : true,
                tags: Array.isArray(event.tags) ? event.tags.join(',') : event.tags,
                latitude: event.latitude || event.coordinates?.lat,
                longitude: event.longitude || event.coordinates?.lon
              };
              
              // Trouver l'ID de la catégorie si seulement le nom est fourni
              if (typeof eventData.category === 'string') {
                const categoryFound = categories.find((c: any) => c.name.toLowerCase() === eventData.category.toLowerCase());
                if (categoryFound) {
                  event.categoryId = categoryFound.id;
                } else {
                  event.categoryId = 1; // Catégorie par défaut
                }
              }
              
              // Créer l'événement
              const createdEvent = await this.importEvent(event);
              results.success.push(createdEvent);
            } catch (error: any) {
              // Enregistrer l'erreur
              results.errors.push({
                row: events.indexOf(event) + 1,
                event: event.title || 'Sans titre',
                error: error.message || 'Erreur inconnue'
              });
            }
          }
          
          resolve(results);
        } catch (error) {
          console.error('Erreur lors du traitement du fichier JSON:', error);
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        console.error('Erreur de lecture du fichier JSON:', error);
        reject(error);
      };
      
      reader.readAsText(file);
    });
  }
};
